import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Ticket Document ---

export interface Ticket {
  id: string;
  tenant_id: string;
  subject: string;
  description: string;
  type: string;
  priority: string;
  status: string;
  assigned_to: string;
  customer_id: string;
  contact_id: string;
  tags: string[];
  resolution: string;
  sla_due_at: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Ticket Comment Document ---

export interface TicketAttachment {
  file_name: string;
  file_url: string;
  file_size: number;
  content_type: string;
}

export interface TicketComment {
  id: string;
  tenant_id: string;
  ticket_id: string;
  author_id: string;
  content: string;
  is_internal: boolean;
  attachments: TicketAttachment[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Create/Update DTOs ---

export interface CreateTicketDto {
  subject: string;
  description?: string;
  type: string;
  priority: string;
  assigned_to?: string;
  customer_id: string;
  contact_id?: string;
  tags?: string[];
  sla_due_at?: string;
}

export interface UpdateTicketDto {
  subject: string;
  description?: string;
  type: string;
  priority: string;
  status?: string;
  assigned_to?: string;
  customer_id: string;
  contact_id?: string;
  tags?: string[];
  resolution?: string;
  sla_due_at?: string;
}

export interface AssignTicketDto {
  assigned_to: string;
}

export interface TicketAttachmentDto {
  file_name: string;
  file_url: string;
  file_size: number;
  content_type: string;
}

export interface CreateTicketCommentDto {
  content: string;
  is_internal: boolean;
  attachments?: TicketAttachmentDto[];
}

export interface UpdateTicketCommentDto {
  content: string;
  is_internal: boolean;
  attachments?: TicketAttachmentDto[];
}

// --- Filter Types ---

export interface TicketFilters extends SearchParams {
  type?: string;
  priority?: string;
  status?: string;
  assigned_to?: string;
  customer_id?: string;
  contact_id?: string;
}

export interface TicketCommentFilters extends SearchParams {
  is_internal?: string;
}

// ========== Ticket API Functions ==========

export async function getTickets(params?: TicketFilters) {
  return apiGet<PagedResponse<Ticket>>('/api/v1/tickets', params);
}

export async function getTicket(id: string) {
  return apiGet<Ticket>(`/api/v1/tickets/${id}`);
}

export async function createTicket(data: CreateTicketDto) {
  return apiPost<Ticket>('/api/v1/tickets', data);
}

export async function updateTicket(id: string, data: UpdateTicketDto) {
  return apiPut<Ticket>(`/api/v1/tickets/${id}`, data);
}

export async function deleteTicket(id: string) {
  return apiDelete<void>(`/api/v1/tickets/${id}`);
}

// ========== Ticket Lifecycle ==========

export async function assignTicket(id: string, data: AssignTicketDto) {
  return apiPost<Ticket>(`/api/v1/tickets/${id}/assign`, data);
}

export async function closeTicket(id: string) {
  return apiPost<Ticket>(`/api/v1/tickets/${id}/close`);
}

export async function reopenTicket(id: string) {
  return apiPost<Ticket>(`/api/v1/tickets/${id}/reopen`);
}

// ========== Comment API Functions ==========

export async function getTicketComments(ticketId: string, params?: TicketCommentFilters) {
  return apiGet<PagedResponse<TicketComment>>(`/api/v1/tickets/${ticketId}/comments`, params);
}

export async function getTicketComment(ticketId: string, commentId: string) {
  return apiGet<TicketComment>(`/api/v1/tickets/${ticketId}/comments/${commentId}`);
}

export async function createTicketComment(ticketId: string, data: CreateTicketCommentDto) {
  return apiPost<TicketComment>(`/api/v1/tickets/${ticketId}/comments`, data);
}

export async function updateTicketComment(ticketId: string, commentId: string, data: UpdateTicketCommentDto) {
  return apiPut<TicketComment>(`/api/v1/tickets/${ticketId}/comments/${commentId}`, data);
}

export async function deleteTicketComment(ticketId: string, commentId: string) {
  return apiDelete<void>(`/api/v1/tickets/${ticketId}/comments/${commentId}`);
}
